import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSiteContent } from "@/lib/siteContent";
import { toast } from "sonner";
import {
  Trash2, Plus, LogOut, UserX, UserCheck,
  Home as HomeIcon, Users, Castle, MapPin, Layers, Layout, Mail, UserCog, BookOpen,
} from "lucide-react";

import CategoriesManager from "@/pages/CategoriesManager";
import StaysManager from "@/pages/admin/StaysManager";
import ContactSettings from "@/pages/admin/ContactSettings";
import HomeEditor from "@/pages/admin/HomeEditor";

/* ---------------- Login Gate ---------------- */
function LoginGate({ onOK }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", { password: pw });
      onOK();
    } catch {
      toast.error("Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
      <form onSubmit={submit} className="w-full max-w-sm px-6" data-testid="admin-login-form">
        <div className="overline text-[#D4AF37]">Admin</div>
        <h1 className="font-serif font-light text-4xl text-[#0A1128] mt-4">Espace éditorial.</h1>
        <p className="mt-4 text-sm text-[#0A1128]/60">Modifier tout le contenu du site — sans code.</p>
        <label className="overline text-[#0A1128]/60 block mt-10">Mot de passe</label>
        <input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)}
          className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37] text-[#0A1128]"
          data-testid="admin-password" />
        <button type="submit" disabled={loading}
          className="mt-8 w-full bg-[#0A1128] text-[#F9F8F6] py-4 text-xs tracking-[0.24em] uppercase hover:bg-[#D4AF37] hover:text-[#0A1128] transition-colors duration-500"
          data-testid="admin-login-submit">
          {loading ? "..." : "Entrer"}
        </button>
      </form>
    </div>
  );
}

/* ---------------- Châteaux (inline) ---------------- */
function ChateauxManager({ chateaux, onChange }) {
  const empty = { name: "", region: "", capacity: 0, price_from: 0, description_fr: "", description_en: "", image_url: "" };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (c) => { setEditing(c.chateau_id); setForm({ ...c }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async () => {
    try {
      if (editing === "new") {
        await api.post("/admin/chateaux", { ...form, capacity: Number(form.capacity), price_from: Number(form.price_from) });
      } else {
        await api.put(`/admin/chateaux/${editing}`, { ...form, capacity: Number(form.capacity), price_from: Number(form.price_from) });
      }
      toast.success("Enregistré");
      cancel();
      onChange();
    } catch { toast.error("Échec"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce château ?")) return;
    await api.delete(`/admin/chateaux/${id}`);
    onChange();
  };
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-serif text-3xl text-[#0A1128]">Nos châteaux partenaires</h2>
          <p className="text-sm text-[#0A1128]/60 mt-1">Liste des châteaux affichés dans l&apos;expérience mariage.</p>
        </div>
        {!editing && (
          <button onClick={startNew} className="bg-[#0A1128] text-[#F9F8F6] px-5 py-2.5 text-xs tracking-[0.24em] uppercase hover:bg-[#D4AF37] hover:text-[#0A1128] transition-colors flex items-center gap-2" data-testid="chateau-new">
            <Plus size={14} /> Ajouter
          </button>
        )}
      </div>
      {editing && (
        <div className="bg-white border border-[#D4AF37]/40 p-6 space-y-4" data-testid="chateau-form">
          <div className="grid md:grid-cols-3 gap-4">
            <input placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="chateau-name" />
            <input placeholder="Région" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm" />
            <input placeholder="URL photo" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm" />
            <input type="number" placeholder="Capacité" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm" />
            <input type="number" placeholder="À partir de (€)" value={form.price_from} onChange={(e) => setForm({ ...form, price_from: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <textarea placeholder="Description FR" rows={4} value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm resize-none" />
            <textarea placeholder="Description EN" rows={4} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm resize-none" />
          </div>
          <div className="flex gap-3">
            <button onClick={save} className="bg-[#0A1128] text-[#F9F8F6] px-6 py-2.5 text-xs tracking-[0.24em] uppercase" data-testid="chateau-save">Enregistrer</button>
            <button onClick={cancel} className="text-xs tracking-[0.24em] uppercase text-[#0A1128]/60 hover:text-[#0A1128]">Annuler</button>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {chateaux.map((c) => (
          <div key={c.chateau_id} className="bg-white border border-[#0A1128]/10 overflow-hidden" data-testid={`chateau-${c.chateau_id}`}>
            {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-48 object-cover" />}
            <div className="p-5">
              <div className="overline text-[#D4AF37]">{c.region}</div>
              <h3 className="font-serif text-2xl text-[#0A1128] mt-1">{c.name}</h3>
              <div className="text-xs text-[#0A1128]/60 mt-2">
                {c.capacity ? `${c.capacity} invités · ` : ""}{c.price_from ? `dès ${c.price_from}€` : ""}
              </div>
              <p className="text-sm text-[#0A1128]/70 mt-3 line-clamp-3">{c.description_fr}</p>
              <div className="flex gap-3 mt-4">
                <button onClick={() => startEdit(c)} className="text-xs tracking-[0.2em] uppercase text-[#0A1128] hover:text-[#D4AF37]">Modifier</button>
                <button onClick={() => remove(c.chateau_id)} className="text-xs tracking-[0.2em] uppercase text-[#E2856E] hover:text-[#0A1128] ml-auto flex items-center gap-1" data-testid={`chateau-del-${c.chateau_id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {chateaux.length === 0 && <div className="text-[#0A1128]/50 italic">Aucun château. Cliquez sur « Ajouter ».</div>}
      </div>
    </div>
  );
}

/* ---------------- Team Accounts (inline) ---------------- */
function TeamManager() {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try { const r = await api.get("/admin/team"); setMembers(r.data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Mot de passe : 8 caractères minimum"); return; }
    setCreating(true);
    try {
      await api.post("/admin/team", form);
      toast.success("Compte créé");
      setForm({ name: "", email: "", password: "", role: "member" });
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Échec");
    } finally { setCreating(false); }
  };
  const toggleActive = async (m) => {
    await api.patch(`/admin/team/${m.member_id}`, { active: !m.active });
    toast.success(m.active ? "Compte désactivé" : "Compte réactivé");
    load();
  };
  const remove = async (m) => {
    if (!window.confirm(`Supprimer le compte de ${m.name} ?`)) return;
    await api.delete(`/admin/team/${m.member_id}`);
    toast.success("Compte supprimé");
    load();
  };
  const resetPassword = async (m) => {
    const pw = window.prompt(`Nouveau mot de passe pour ${m.name} (8+ caractères)`);
    if (!pw) return;
    if (pw.length < 8) { toast.error("Trop court"); return; }
    await api.patch(`/admin/team/${m.member_id}`, { password: pw });
    toast.success("Mot de passe mis à jour");
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-3xl text-[#0A1128]">Comptes équipe</h2>
        <p className="text-sm text-[#0A1128]/60 mt-1">Gérez les identifiants des membres de l&apos;équipe (accès au tableau de bord pro).</p>
      </div>
      <form onSubmit={create} className="bg-white border border-[#D4AF37]/40 p-6" data-testid="team-create-form">
        <div className="overline text-[#D4AF37] mb-4">Créer un compte</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input required placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="team-new-name" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="team-new-email" />
          <input required type="password" placeholder="Mot de passe (8+)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="team-new-password" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm">
            <option value="member">Membre</option>
            <option value="manager">Manager</option>
          </select>
          <button type="submit" disabled={creating} className="bg-[#0A1128] text-[#F9F8F6] px-4 py-2 text-xs tracking-[0.24em] uppercase flex items-center justify-center gap-2 disabled:opacity-60" data-testid="team-new-submit">
            <Plus size={14} /> {creating ? "..." : "Ajouter"}
          </button>
        </div>
      </form>
      <div className="space-y-3">
        {members.length === 0 && <div className="text-[#0A1128]/50 italic">Aucun membre.</div>}
        {members.map((m) => (
          <div key={m.member_id} className={`bg-white border p-5 grid md:grid-cols-5 gap-4 items-center ${m.active ? "border-[#0A1128]/10" : "border-[#E2856E]/30 bg-[#E2856E]/5"}`} data-testid={`team-member-${m.member_id}`}>
            <div>
              <div className="font-serif text-lg text-[#0A1128]">{m.name}</div>
              <div className="text-xs text-[#0A1128]/60">{m.email}</div>
            </div>
            <div className="text-xs text-[#0A1128]/70 uppercase tracking-wide">{m.role}</div>
            <div>
              <span className={`inline-block px-3 py-1 text-xs tracking-wide ${m.active ? "bg-[#D4AF37]/15 text-[#0A1128]" : "bg-[#E2856E]/20 text-[#0A1128]/70"}`}>
                {m.active ? "Actif" : "Désactivé"}
              </span>
            </div>
            <div className="text-xs text-[#0A1128]/50">{new Date(m.created_at).toLocaleDateString("fr-FR")}</div>
            <div className="flex gap-4 justify-end">
              <button onClick={() => resetPassword(m)} className="text-xs tracking-wide uppercase text-[#0A1128]/70 hover:text-[#D4AF37]" data-testid={`team-reset-${m.member_id}`}>Reset mdp</button>
              <button onClick={() => toggleActive(m)} className={`text-xs tracking-wide uppercase flex items-center gap-1 ${m.active ? "text-[#E2856E] hover:text-[#0A1128]" : "text-[#0A1128] hover:text-[#D4AF37]"}`} data-testid={`team-toggle-${m.member_id}`}>
                {m.active ? (
                  <span className="inline-flex items-center gap-1"><UserX size={14} /> Désactiver</span>
                ) : (
                  <span className="inline-flex items-center gap-1"><UserCheck size={14} /> Réactiver</span>
                )}
              </button>
              <button onClick={() => remove(m)} className="text-[#E2856E] hover:text-[#0A1128]" data-testid={`team-delete-${m.member_id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- About (delegates to CategoriesManager filtered to slug=about) ---------------- */
function AboutEditor({ onSitePublicRefresh }) {
  // Simpler: send user to CategoriesManager which shows the seeded 'about' category first.
  return (
    <div>
      <div className="mb-6">
        <h2 className="font-serif text-3xl text-[#0A1128]">Notre histoire</h2>
        <p className="text-sm text-[#0A1128]/60 mt-1">Membres de l&apos;équipe, titre et introduction de la page « Notre histoire ».</p>
      </div>
      <CategoriesManager onSitePublicRefresh={onSitePublicRefresh} />
    </div>
  );
}

/* ---------------- Newsletter (read-only list) ---------------- */
function NewsletterView() {
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    api.get("/newsletter/subscribers").then((r) => setSubs(r.data)).catch(() => {});
  }, []);
  return (
    <div>
      <h2 className="font-serif text-3xl text-[#0A1128] mb-4">Newsletter</h2>
      <div className="bg-white border border-[#0A1128]/10 p-6">
        <div className="overline text-[#D4AF37] mb-3">Abonnés ({subs.length})</div>
        <ul className="divide-y divide-[#0A1128]/10">
          {subs.map((s) => (
            <li key={s.sub_id} className="py-3 flex justify-between text-sm">
              <span>{s.email}</span>
              <span className="text-[#0A1128]/50">{s.segment} · {s.language}</span>
            </li>
          ))}
          {subs.length === 0 && <li className="text-[#0A1128]/50 italic py-3">Aucun abonné pour l&apos;instant.</li>}
        </ul>
      </div>
    </div>
  );
}

/* ---------------- Root Admin (sidebar shell) ---------------- */
const SECTIONS = [
  { id: "home", label: "Accueil", icon: HomeIcon },
  { id: "about", label: "Notre histoire", icon: Users },
  { id: "stays", label: "Séjours & formules", icon: Layers },
  { id: "chateaux", label: "Châteaux", icon: Castle },
  { id: "categories", label: "Autres catégories", icon: Layout },
  { id: "contact", label: "Contact & pied", icon: Mail },
  { id: "team", label: "Comptes équipe", icon: UserCog },
  { id: "newsletter", label: "Newsletter", icon: BookOpen },
];

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [section, setSection] = useState("home");
  const { chateaux, refresh } = useSiteContent();

  useEffect(() => {
    api.get("/admin/verify").then(() => setAuthed(true)).catch(() => setAuthed(false)).finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    await api.post("/admin/logout");
    setAuthed(false);
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-[#0A1128]/60 font-serif italic">…</div>;
  if (!authed) return <LoginGate onOK={() => { setAuthed(true); refresh(); }} />;

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex">
      {/* Sidebar */}
      <aside className="w-72 bg-white border-r border-[#0A1128]/10 flex flex-col sticky top-0 h-screen" data-testid="admin-sidebar">
        <div className="px-6 py-6 border-b border-[#0A1128]/10">
          <div className="overline text-[#D4AF37]">Admin</div>
          <div className="font-serif text-2xl text-[#0A1128] leading-tight">Espace éditorial</div>
          <div className="text-xs text-[#0A1128]/50 mt-1">French Heritage Experiences</div>
        </div>
        <nav className="flex-1 py-4">
          {SECTIONS.map(({ id, label, icon: Icon }) => {
            const active = section === id;
            return (
              <button
                key={id}
                onClick={() => setSection(id)}
                className={`w-full text-left px-6 py-3 flex items-center gap-3 text-sm transition-colors border-l-2 ${active ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#0A1128]" : "border-transparent text-[#0A1128]/70 hover:text-[#0A1128] hover:bg-[#0A1128]/[0.03]"}`}
                data-testid={`admin-nav-${id}`}
              >
                <Icon size={16} className={active ? "text-[#D4AF37]" : "text-[#0A1128]/50"} />
                <span className={active ? "font-medium" : ""}>{label}</span>
              </button>
            );
          })}
        </nav>
        <div className="border-t border-[#0A1128]/10 px-6 py-4 flex items-center justify-between text-xs">
          <a href="/" className="text-[#0A1128]/70 hover:text-[#D4AF37] tracking-[0.2em] uppercase" data-testid="admin-goto-public">Voir le site</a>
          <button onClick={logout} className="text-[#0A1128]/70 hover:text-[#0A1128] tracking-[0.2em] uppercase flex items-center gap-2" data-testid="admin-logout">
            <LogOut size={14} /> Sortir
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 px-8 md:px-12 py-10 overflow-x-hidden" data-testid="admin-main">
        {section === "home" && <HomeEditor onSitePublicRefresh={refresh} />}
        {section === "about" && <AboutEditor onSitePublicRefresh={refresh} />}
        {section === "stays" && <StaysManager onSitePublicRefresh={refresh} />}
        {section === "chateaux" && <ChateauxManager chateaux={chateaux} onChange={refresh} />}
        {section === "categories" && <CategoriesManager onSitePublicRefresh={refresh} />}
        {section === "contact" && <ContactSettings onSitePublicRefresh={refresh} />}
        {section === "team" && <TeamManager />}
        {section === "newsletter" && <NewsletterView />}
      </div>
    </div>
  );
}
